import { api } from "./api.js";
import type { TicketSla } from "./sla.js";

// ── Types ───────────────────────────────────────────────────────────────────
export type TicketStatus = "open" | "in_progress" | "waiting" | "resolved" | "closed";
export type TicketPriority = "low" | "normal" | "high" | "urgent";
export type AuthorType = "staff" | "client" | "bot";

export interface ClientRef { id: string; name: string; kind: string }
/** The juhuklient a ticket is about, when there is no registered client yet. */
export interface LeadRef { id: string; name: string; kind: string; status: string }

/**
 * The call a ticket came out of - today always a voicemail.
 *
 * Enough for a badge and no more: the badge says what kind of call it was and
 * when, and links to the call pane for everything else.
 */
export interface TicketCallRef {
  id: string;
  direction: "outbound" | "inbound";
  voicemail: boolean;
  startedAt: string;
  durationSec: number | null;
  fromNumber: string | null;
  toNumber: string;
  recordingStatus: string;
}

/**
 * One file on a ticket message.
 *
 * The same shape the mailbox uses, and the same promise: the storage key never
 * travels. `stored` is the one bit of it the browser is given - whether there
 * are bytes to fetch - and the fetch itself goes by INDEX through
 * `ticketAttachmentUrl`, behind the session.
 */
export interface TicketAttachment {
  name: string;
  contentType: string;
  size: number;
  /** False on a record whose bytes were never kept: a paperclip, not a download. */
  stored?: boolean;
}

export interface TicketMessage {
  id: string;
  ticketId: string;
  author: string;
  authorType: AuthorType;
  body: string;
  internal: boolean;
  attachments: TicketAttachment[] | null;
  createdAt: string;
}

/**
 * A task somebody made out of a ticket, as the ticket page draws it.
 *
 * The task's own fields, not a copy of the ticket's: `status` is what the tasks
 * register says today, so ticking the work off in the register is immediately
 * true on the ticket too. Two places holding "is this done" is two places that
 * disagree the first time somebody closes the task somewhere else, so the ticket
 * keeps no flag of its own.
 */
export interface TicketTask {
  id: string;
  title: string;
  status: "todo" | "in_progress" | "review" | "done";
  priority: TicketPriority;
  dueDate: string | null;
  completedAt: string | null;
  assignees: string[];
  projectId: string | null;
}

export interface Ticket {
  id: string;
  number: string;
  clientId: string | null;
  /** Both party ids can be set at once - see the API's `lib/ticketShape.ts`. */
  leadId: string | null;
  /** The call this came from. Null on every ticket that was not a voicemail. */
  callLogId: string | null;
  contactName: string | null;
  contactEmail: string | null;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  department: string | null;
  assignedTo: string | null;
  source: string | null;
  projectId: string | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  /** The desk's own labels, the same vocabulary projects and tasks use. */
  tags: string[];
  createdAt: string;
  updatedAt: string;
  client?: ClientRef | null;
  lead?: LeadRef | null;
  callLog?: TicketCallRef | null;
  messages?: TicketMessage[];
  /**
   * The tasks filed from this ticket. Present on the detail, absent on a list
   * row - so an empty array means "none yet" and `undefined` means "this ticket
   * came from an endpoint that does not carry them", which are different things
   * and must not both draw an empty card.
   */
  tasks?: TicketTask[];
  _count?: { messages: number };
  /**
   * The two response-time clocks, judged on the server against the desk's
   * promises (`lib/sla.ts` there, `lib/sla.ts` here for how to read one).
   *
   * Optional because the routes that CREATE and PATCH a ticket answer with the
   * bare row - no clocks - and only the list and the detail carry them. A
   * missing `sla` draws no badge, which is also what a desk with no promises
   * gets, and the two cases want the same nothing.
   */
  sla?: TicketSla;
}

/** The party a ticket is filed against, whichever of the two tables it is in. */
export function ticketParty(ticket: Ticket): { kind: "client" | "lead"; id: string; name: string } | null {
  // The client wins when both are set, which happens after a lead is converted:
  // the customer relationship is the more specific fact, and the lead link stays
  // on the row so the trail back to the call survives.
  if (ticket.client) return { kind: "client", id: ticket.client.id, name: ticket.client.name };
  if (ticket.lead) return { kind: "lead", id: ticket.lead.id, name: ticket.lead.name };
  return null;
}

export interface TicketStats {
  byStatus: Partial<Record<TicketStatus, number>>;
  mine: number;
  open: number;
}

export interface CannedReply {
  id: string;
  title: string;
  body: string;
  group: string | null;
  createdAt: string;
}

interface Page<T> { rows: T[]; total: number; page: number; pageSize: number; pageCount: number }

/**
 * The ticket page, plus the one fact the list needs about the desk as a whole.
 *
 * `slaConfigured` is false until somebody sets a promise, and the screen hides
 * the SLA filter entirely rather than offering a control that can only ever
 * return an empty page.
 */
interface TicketPage extends Page<Ticket> { slaConfigured: boolean }

function qs(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return "";
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

// ── Tickets ───────────────────────────────────────────────────────────────────
export const getTickets = (params?: Record<string, string | number | boolean | undefined>) =>
  api.get<TicketPage>(`/admin/crm/tickets${qs(params)}`);
export const getTicketStats = () => api.get<TicketStats>("/admin/crm/tickets/stats");
export const getTicket = (id: string) =>
  api.get<Ticket & { messages: TicketMessage[] }>(`/admin/crm/tickets/${id}`);
export const createTicket = (body: Record<string, unknown>) =>
  api.post<Ticket>("/admin/crm/tickets", body);
export const updateTicket = (id: string, body: Record<string, unknown>) =>
  api.patch<Ticket>(`/admin/crm/tickets/${id}`, body);
export const deleteTicket = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/tickets/${id}`);

export const postTicketMessage = (
  id: string,
  body: {
    body: string; internal?: boolean; status?: TicketStatus;
    /**
     * Ids of files staged through the shared attachment routes, never storage
     * keys. The server looks the keys up behind its own ownership check.
     */
    attachmentIds?: string[];
  },
) => api.post<TicketMessage>(`/admin/crm/tickets/${id}/messages`, body);

/**
 * Where one file on a ticket message is fetched from.
 *
 * By index into that message's own list, which is what the server accepts: a
 * URL carrying a storage key would be a client's file reachable without a
 * session, because the bucket behind it is public-read.
 */
export const ticketAttachmentUrl = (ticketId: string, messageId: string, index: number) =>
  `/api/admin/crm/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}/attachments/${index}`;
export const deleteTicketMessage = (id: string, mid: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/tickets/${id}/messages/${mid}`);

export const suggestReply = (id: string) =>
  api.post<{ text: string }>(`/admin/crm/tickets/${id}/suggest-reply`, {});

// ── The ticket as work ────────────────────────────────────────────────────────
/**
 * File this ticket as a task.
 *
 * Every field is optional and every one of them OVERRIDES something the server
 * works out from the ticket itself (the subject, the client, the assignee, the
 * priority, the opening message, the project). An empty body is therefore a
 * valid "file it exactly as it stands".
 *
 * Needs `manage:crm.projects` as well as the support right, because it creates a
 * task - the same two-permission split the call pane's "make a task" uses. Hide
 * the control from an operator without it rather than letting them press a
 * button that can only answer 403.
 */
export const createTicketTask = (id: string, body: Record<string, unknown> = {}) =>
  api.post<TicketTask & { ticketId: string }>(`/admin/crm/tickets/${id}/tasks`, body);

/** The tasks made from this ticket. The detail already carries them; this is how
 *  the card refreshes after the dialog has added one, without re-reading the
 *  whole conversation underneath it. */
export const getTicketTasks = (id: string) =>
  api.get<{ rows: TicketTask[] }>(`/admin/crm/tickets/${id}/tasks`);

// ── Canned replies ─────────────────────────────────────────────────────────────
export const getCannedReplies = (q?: string) =>
  api.get<{ rows: CannedReply[] }>(`/admin/crm/canned-replies${qs({ q })}`);
export const createCannedReply = (body: Record<string, unknown>) =>
  api.post<CannedReply>("/admin/crm/canned-replies", body);
export const updateCannedReply = (id: string, body: Record<string, unknown>) =>
  api.patch<CannedReply>(`/admin/crm/canned-replies/${id}`, body);
export const deleteCannedReply = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/canned-replies/${id}`);

// ── Staff (the assignee picker) ────────────────────────────────────────────────
/**
 * One endpoint, one type - see `lib/staff.ts`. This module used to declare its
 * own `{ id, name, email }` for the same route, which typechecked and quietly
 * dropped the `active` flag the assignee pickers needed in order to stop
 * offering people who had left.
 *
 * `StaffMember` stays alive as the alias because five screens import that name;
 * renaming them is a separate job with a separate diff, and the point of the fix
 * was to end the second DESCRIPTION, not the second word for it.
 */
export { getStaff } from "./staff.js";
export type { StaffUser as StaffMember } from "./staff.js";

/**
 * Departments already in use, commonest first, topped up with the built-in
 * three. What the department combobox opens with - the column itself stays free
 * text, so this is a shortcut and never a constraint.
 */
export const getDepartments = () => api.get<{ rows: string[] }>("/admin/crm/tickets/departments");

/** The labels already in use on tickets - the options the queue's filter offers. */
export const getTicketTags = () => api.get<{ rows: string[] }>("/admin/crm/ticket-tags");
